import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { ApiError, documentsApi, foldersApi, usingMockApi } from '../lib/api';
import { useAuth } from '../context/auth';
import { useAdmin } from '../context/admin';
import { useQueryNav } from '../lib/nav';
import type { DocumentPage, DocumentRow, Folder, WellTags } from '../lib/types';
import { SkeletonLog, SkeletonPage, SkeletonRows, SkeletonTree } from '../components/ui/Skeleton';

/**
 * pdf.js is a megabyte of JavaScript. Loading it with the rest of the app would
 * make the sign-in page pay for a viewer nobody has opened yet, so it arrives
 * the first time a document is selected.
 */
const DocPreview = lazy(async () => ({
  default: (await import('./DocPreview')).DocPreview,
}));

/** Likewise the log viewer, which only matters to whoever opens it. */
const LogViewer = lazy(async () => ({
  default: (await import('./logview/LogViewer')).LogViewer,
}));

/**
 * The Documents section: the well's folder tree on the left, one folder's
 * documents on the right.
 *
 * Both come from the API. The tree's shape is the superadmin's — nothing here
 * assumes "Operator" or any other name exists — and a well that was never given
 * a template simply has no folders, which is a normal state rather than an
 * error.
 *
 * A folder says where a document lives; its tags say what data is in it, and
 * a document can hold several things. Whoever files the well's documents tags
 * them, and anyone can list the well's documents by a tag, wherever in the
 * tree they are filed. The assistant reads the tags too.
 */

interface Row {
  folder: Folder;
  depth: number;
}

/** Depth-first, so the tree renders as an indented list. */
function flatten(folders: Folder[], depth = 1): Row[] {
  return folders.flatMap((folder) => [
    { folder, depth },
    ...flatten(folder.children ?? [], depth + 1),
  ]);
}

const PER_PAGE = 4;

function formatWhen(iso: string | null) {
  if (!iso) return '—';

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

export function Documents({ wellId, wellName }: { wellId: number; wellName: string }) {
  // Which folder is open and which page it is on live in the URL, so a refresh
  // or a shared link lands back in the same place.
  const { params, set } = useQueryNav();

  // Only the superadmin arranges the tree. Hiding the controls is a courtesy
  // to everyone else; the server checks the role again on every call.
  const { user } = useAuth();
  const { access } = useAdmin();
  // While a superadmin looks at the portal as somebody else, the controls
  // follow that person's role, not the superadmin's own.
  const mayArrange = user?.role === 'superadmin' && !access.preview;

  // What the folder toolbar is doing right now, if anything.
  const [action, setAction] = useState<'create' | 'rename' | 'delete' | 'describe' | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloads, setReloads] = useState(0);

  // An upload in flight: which file, and how far through the batch we are.
  const [upload, setUpload] = useState<{ done: number; total: number; name: string } | null>(null);

  // Upload failures are kept apart from `error`: finishing an upload reloads
  // the list, and that reload clears `error` on success — which would wipe the
  // very message explaining why a file did not make it.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // The well's tags, with how many documents carry each. Null until read, and
  // left null against an API that has no tags yet — the bar then stays away.
  const [wellTags, setWellTags] = useState<WellTags | null>(null);
  const [tagReloads, setTagReloads] = useState(0);
  // The document whose tags are being edited, if any.
  const [tagging, setTagging] = useState<number | null>(null);

  const [tree, setTree] = useState<Folder[] | null>(null);
  const [page, setPage] = useState<DocumentPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openFile, setOpenFile] = useState<DocumentRow | null>(null);

  const rows = useMemo(() => flatten(tree ?? []), [tree]);

  // The log viewer is not a folder: it sits in the tree, but draws the well's
  // LAS and ASCII files from wherever they are filed.
  const showLogs = params.get('view') === 'logs';

  // ?tag=Mud lists the well's documents carrying that tag, from every folder.
  const tag = showLogs ? '' : (params.get('tag') ?? '').trim();

  const requested = Number(params.get('folder'));
  const selected =
    rows.find((r) => r.folder.id === requested)?.folder ??
    rows.find((r) => r.folder.documentCount > 0)?.folder ??
    rows[0]?.folder ??
    null;

  // Filing documents is the admin's job on the wells assigned to them, and the
  // superadmin's everywhere. Smart folders hold nothing — their contents are a
  // saved search — so there is nowhere to put a file.
  const mayFile =
    access.kind === 'role'
      ? access.can('documents.manage')
      : user?.role === 'superadmin' ||
        (user?.role === 'admin' && (user.wells ?? []).includes(wellId));
  const canUpload = Boolean(selected && selected.kind === 'folder' && mayFile);

  const uploadHint = canUpload
    ? `Upload into ${selected?.name}`
    : !mayFile
      ? 'Only an admin assigned to this well can upload documents'
      : selected?.kind === 'smart'
        ? 'This folder is a saved search — pick a real folder to upload into'
        : 'Pick a folder first';

  const pageNo = Math.max(1, Number(params.get('page')) || 1);
  const pageCount = page ? Math.max(1, Math.ceil(page.total / page.perPage)) : 1;

  useEffect(() => {
    let cancelled = false;

    foldersApi
      .tree(wellId)
      .then((res) => {
        if (!cancelled) setTree(res.items);
      })
      .catch((err) => {
        if (!cancelled) {
          setTree([]);
          setError(err instanceof ApiError ? err.message : 'Could not load the folder tree.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [wellId, reloads]);

  useEffect(() => {
    let cancelled = false;

    documentsApi
      .tags(wellId)
      .then((res) => {
        if (!cancelled) setWellTags(res);
      })
      .catch(() => {
        if (!cancelled) setWellTags(null);
      });

    return () => {
      cancelled = true;
    };
  }, [wellId, reloads, tagReloads]);

  useEffect(() => {
    if (showLogs || (!tag && !selected)) return;
    let cancelled = false;

    (tag
      ? documentsApi.listByTag(wellId, tag, pageNo, PER_PAGE)
      : documentsApi.list((selected as Folder).id, pageNo, PER_PAGE)
    )
      .then((res) => {
        if (cancelled) return;
        setPage(res);

        // A citation from the assistant names a document; open that one if it
        // is on this page, the first otherwise.
        const wanted = Number(params.get('doc'));
        setOpenFile(res.items.find((d) => d.id === wanted) ?? res.items[0] ?? null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPage(null);
        setError(
          err instanceof ApiError
            ? err.message
            : tag
              ? 'Could not load the documents with this tag.'
              : 'Could not load this folder.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [selected, pageNo, showLogs, tag, wellId]);

  // A second citation into the same folder changes only ?doc=, which the fetch
  // above does not watch. Follow it here, without refetching the page.
  const wantedDoc = Number(params.get('doc'));
  useEffect(() => {
    if (!page || !wantedDoc) return;
    const hit = page.items.find((d) => d.id === wantedDoc);
    if (hit) setOpenFile(hit);
  }, [wantedDoc, page]);

  // Opening a folder is navigation and goes into history; paging replaces the
  // entry, because nobody wants Back to walk them through five pages of a list.
  const selectFolder = (id: number) => {
    setUploadError(null);
    setTagging(null);
    set({ folder: String(id), page: null, view: null, tag: null });
  };

  // A tag is a way of looking at the well, like a folder is: picking one goes
  // into history, and picking it again goes back to the folder.
  const selectTag = (next: string | null) => {
    setTagging(null);
    set({ tag: next && next.toLowerCase() !== tag.toLowerCase() ? next : null, page: null, doc: null });
  };

  // Saved tags come back spelled the way the company spells them. The row is
  // patched where it stands, so the open document stays open; only the tag
  // counts are read again.
  const saveTags = async (doc: DocumentRow, tags: string[]) => {
    const saved = await documentsApi.setTags(doc.id, tags);
    setPage((have) =>
      have ? { ...have, items: have.items.map((d) => (d.id === saved.id ? { ...d, ...saved } : d)) } : have,
    );
    setOpenFile((have) => (have?.id === saved.id ? { ...have, ...saved } : have));
    setTagging(null);
    setTagReloads((n) => n + 1);
  };

  const folderName = (id: number) => rows.find((r) => r.folder.id === id)?.folder.name;
  const knownTags = useMemo(
    () => [...(wellTags?.items.map((t) => t.tag) ?? []), ...(wellTags?.suggestions ?? [])],
    [wellTags],
  );
  const openLogs = () => set({ view: 'logs', page: null, doc: null });
  const goToPage = (n: number) => {
    const clamped = Math.min(pageCount, Math.max(1, n));
    set({ page: clamped === 1 ? null : String(clamped) }, { replace: true });
  };

  const refresh = useCallback(() => setReloads((n) => n + 1), []);

  const startAction = (kind: 'create' | 'rename' | 'delete' | 'describe') => {
    setError(null);
    setAction(kind);
    setDraft(
      kind === 'rename'
        ? (selected?.name ?? '')
        : kind === 'describe'
          ? (selected?.description ?? '')
          : '',
    );
  };

  const cancelAction = () => {
    setAction(null);
    setDraft('');
  };

  // One handler for the three of them: they differ only in the call they make
  // and in what they leave selected afterwards.
  const submitAction = async () => {
    if (!action || busy) return;

    setBusy(true);
    setError(null);

    try {
      if (action === 'delete') {
        if (!selected) return;
        await foldersApi.remove(selected.id);
        set({ folder: null, page: null }, { replace: true });
      } else if (action === 'create') {
        // A new folder goes inside the selected one, or at the root of the
        // well when the selection is a smart folder — nothing can live in
        // those, so the sensible parent is the root.
        const parentId = selected && selected.kind !== 'smart' ? selected.id : null;
        const created = await foldersApi.create(wellId, { name: draft, parentId });
        set({ folder: String(created.id), page: null });
      } else if (action === 'describe') {
        if (!selected) return;
        await foldersApi.describe(selected.id, draft);
      } else {
        if (!selected) return;
        await foldersApi.rename(selected.id, draft);
      }

      cancelAction();
      refresh();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'That did not work. Try a different name.',
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Upload, one file at a time.
   *
   * Three steps per file: the API creates the row and signs a URL, the browser
   * PUTs the bytes straight to object storage — the server never sees them —
   * and then the row is marked complete. A file that fails part way through
   * leaves a pending row, which no listing shows.
   */
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !selected) return;

      setUploadError(null);
      const failures: string[] = [];

      for (const [i, file] of files.entries()) {
        setUpload({ done: i, total: files.length, name: file.name });

        try {
          const ticket = await documentsApi.startUpload(selected.id, {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
          });

          // In mock mode there is no bucket to PUT to; the ticket is enough.
          if (!usingMockApi) {
            let res: Response;
            try {
              res = await fetch(ticket.uploadUrl, {
                method: 'PUT',
                // Exactly the signed content type, or the signature is void.
                headers: { 'Content-Type': ticket.contentType },
                body: file,
              });
            } catch {
              throw new ApiError(
                'the browser could not reach object storage — the bucket needs a CORS rule allowing this site (PUT, GET, HEAD)',
                0,
              );
            }
            if (!res.ok) {
              // OSS answers errors as XML with a short machine code —
              // SignatureDoesNotMatch, AccessDenied, EntityTooLarge. Carrying
              // it through is the difference between "it failed" and knowing
              // which knob is wrong.
              const detail = await res.text().catch(() => '');
              const code = /<Code>([^<]+)<\/Code>/.exec(detail)?.[1];

              throw new ApiError(
                `object storage refused the upload (${res.status}${code ? ` ${code}` : ''})`,
                res.status,
              );
            }
          }

          await documentsApi.completeUpload(ticket.document.id, file.size);
        } catch (err) {
          failures.push(
            `${file.name}: ${err instanceof ApiError ? err.message : 'upload failed'}`,
          );
        }
      }

      setUpload(null);
      if (failures.length > 0) setUploadError(failures.join(' · '));

      // Refetch the tree as well as the list: the folder counts just changed.
      refresh();
    },
    [selected, refresh],
  );

  const download = useCallback(async (doc: DocumentRow) => {
    try {
      const { url } = await documentsApi.downloadUrl(doc.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(
        err instanceof ApiError && (err.status === 503 || err.status === 404)
          ? 'Downloads need object storage (OSS) configured on the server.'
          : 'Could not get a download link for this document.',
      );
    }
  }, []);

  return (
    <div className="docsview">
      <aside className="doctree" aria-label="Document folders">
        {mayArrange && (
          <div className="treetools">
            <button type="button" className="treetool" onClick={() => startAction('create')}>
              + Folder
            </button>
            <button
              type="button"
              className="treetool"
              onClick={() => startAction('rename')}
              disabled={!selected}
            >
              Rename
            </button>
            <button
              type="button"
              className="treetool"
              onClick={() => startAction('describe')}
              disabled={!selected || selected.kind === 'smart'}
              title="Tell the assistant what this folder holds"
            >
              About
            </button>
            <button
              type="button"
              className="treetool treetool--danger"
              onClick={() => startAction('delete')}
              disabled={!selected}
            >
              Delete
            </button>
          </div>
        )}

        {action === 'delete' && selected && (
          <div className="treeform" role="alertdialog" aria-label="Delete folder">
            <p className="treeform-warn">
              Delete <strong>{selected.name}</strong>, everything inside it and its documents?
            </p>
            <div className="treeform-actions">
              <button type="button" className="treetool treetool--danger" onClick={submitAction} disabled={busy}>
                Delete
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {action === 'describe' && selected && (
          <form
            className="treeform"
            onSubmit={(e) => {
              e.preventDefault();
              void submitAction();
            }}
          >
            <label className="treeform-label">
              What is in {selected.name}?
              <textarea
                className="treeform-input treeform-text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. One Daily Operation Report per day: depth, cost against AFE, NPT, the 24-hour summary and forecast."
                maxLength={2000}
                rows={5}
                autoFocus
              />
            </label>
            <p className="treeform-hint">
              The assistant reads this before anything in the folder. Saving re-reads the folder's
              documents in the background.
            </p>
            <div className="treeform-actions">
              <button type="submit" className="treetool treetool--primary" disabled={busy}>
                Save
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {(action === 'create' || action === 'rename') && (
          <form
            className="treeform"
            onSubmit={(e) => {
              e.preventDefault();
              void submitAction();
            }}
          >
            <label className="treeform-label">
              {action === 'create'
                ? selected && selected.kind !== 'smart'
                  ? `New folder inside ${selected.name}`
                  : 'New folder at the top level'
                : `Rename ${selected?.name ?? ''}`}
              <input
                className="treeform-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Folder name"
                maxLength={255}
                autoFocus
              />
            </label>
            <div className="treeform-actions">
              <button type="submit" className="treetool treetool--primary" disabled={busy || !draft.trim()}>
                {action === 'create' ? 'Create' : 'Save'}
              </button>
              <button type="button" className="treetool" onClick={cancelAction}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <button type="button" className="treenode" style={{ paddingLeft: 8 }} disabled>
          <TreeIcon kind="folder" />
          <span className="treenode-label">{wellName}</span>
        </button>

        {tree !== null && tree.length > 0 && (
          <button
            type="button"
            className={showLogs ? 'treenode treenode--view is-selected' : 'treenode treenode--view'}
            style={{ paddingLeft: 22 }}
            onClick={openLogs}
            title="Draw this well's LAS and ASCII files as a log you can rearrange"
          >
            <TreeIcon kind="log" />
            <span className="treenode-label">Log Viewer</span>
          </button>
        )}

        {tree === null && <SkeletonTree />}

        {tree?.length === 0 && (
          <p className="content-loading">
            This well has no folders yet. A superadmin gives it a structure by applying a template.
          </p>
        )}

        {rows.map(({ folder, depth }) => (
          <button
            key={folder.id}
            type="button"
            className={
              !showLogs && !tag && folder.id === selected?.id ? 'treenode is-selected' : 'treenode'
            }
            style={{ paddingLeft: 8 + depth * 14 }}
            onClick={() => selectFolder(folder.id)}
            title={
              folder.kind === 'smart'
                ? 'Contents come from a saved search'
                : folder.description || undefined
            }
          >
            <TreeIcon kind={folder.kind === 'smart' ? 'clock' : 'folder'} />
            <span className="treenode-label">{folder.name}</span>
            {folder.documentCount > 0 && (
              <span className="treenode-count">({folder.documentCount})</span>
            )}
          </button>
        ))}
      </aside>

      {showLogs && tree ? (
        <section className="doclist" aria-label="Log Viewer">
          <Suspense fallback={<SkeletonLog />}>
            <LogViewer wellId={wellId} tree={tree} />
          </Suspense>
        </section>
      ) : (
      <section className="doclist" aria-label={tag ? `Documents tagged ${tag}` : (selected?.name ?? 'Documents')}>
        {error && (
          <div className="alert alert--page" role="alert">
            {error}
          </div>
        )}

        {wellTags && wellTags.items.length > 0 && (
          <div className="tagbar" role="group" aria-label="Documents by data tag">
            <span className="tagbar-label" title="What data this well's documents were tagged as holding">
              Data
            </span>
            {wellTags.items.map((t) => {
              const on = t.tag.toLowerCase() === tag.toLowerCase();
              return (
                <button
                  key={t.tag}
                  type="button"
                  className={on ? 'tagchip tagchip--pick is-active' : 'tagchip tagchip--pick'}
                  aria-pressed={on}
                  title={
                    on
                      ? 'Back to the folder'
                      : `Every document of this well tagged ${t.tag}, from any folder`
                  }
                  onClick={() => selectTag(t.tag)}
                >
                  {t.tag} <small>{t.count}</small>
                </button>
              );
            })}
          </div>
        )}

        {tag ? (
          <p className="folderabout folderabout--tag">
            Documents of {wellName} tagged <strong>{tag}</strong>, from every folder.{' '}
            <button type="button" className="linkbtn" onClick={() => selectTag(null)}>
              Back to {selected?.name ?? 'the folder'}
            </button>
          </p>
        ) : (
          selected?.description && <p className="folderabout">{selected.description}</p>
        )}

        {uploadError && (
          <div className="alert alert--page" role="alert">
            {uploadError}
          </div>
        )}

        <table className="doctable">
          <thead>
            <tr>
              <th className="doctable-check">
                <input type="checkbox" aria-label="Select all" />
              </th>
              <th>Upload Rig Date/Time</th>
              <th>
                Name <SearchGlyph />
              </th>
              <th className="doctable-tags" title="What data the document holds. The assistant reads these.">
                Data tags
              </th>
              <th>Size (KB)</th>
              <th>
                Ext <SearchGlyph />
              </th>
              <th>Detail</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {page === null && !error && <SkeletonRows rows={4} cols={8} />}
            {page?.items.map((doc) => (
              <Fragment key={doc.id}>
              <tr
                className={doc.id === openFile?.id ? 'is-open' : undefined}
                onClick={() => setOpenFile(doc)}
              >
                <td className="doctable-check">
                  <input
                    type="checkbox"
                    aria-label={`Select ${doc.name}`}
                    checked={doc.id === openFile?.id}
                    onChange={() => setOpenFile(doc)}
                  />
                </td>
                <td className="doctable-when">{formatWhen(doc.uploadedAt)}</td>
                <td className="doctable-name">
                  {doc.name}
                  {tag && folderName(doc.folderId) && (
                    <small className="doctable-folder">in {folderName(doc.folderId)}</small>
                  )}
                  {/* On a phone there is no room for a column of its own. */}
                  <div className="doctable-tags-narrow">
                    <DocTags
                      doc={doc}
                      editable={mayFile}
                      editing={tagging === doc.id}
                      onPick={selectTag}
                      onEdit={() => setTagging(tagging === doc.id ? null : doc.id)}
                    />
                  </div>
                </td>
                <td className="doctable-tags">
                  <DocTags
                    doc={doc}
                    editable={mayFile}
                    editing={tagging === doc.id}
                    onPick={selectTag}
                    onEdit={() => setTagging(tagging === doc.id ? null : doc.id)}
                  />
                  {!mayFile && (doc.tags ?? []).length === 0 && <span className="doctable-none">—</span>}
                </td>
                <td className="doctable-num">{Math.max(1, Math.round(doc.sizeBytes / 1024))}</td>
                <td>{doc.ext || '—'}</td>
                <td>
                  <button type="button" className="tool" title="Detail" aria-label="Detail">
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path
                        d="M3 6h14M3 10h14M3 14h14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="tool"
                    title="Download"
                    aria-label={`Download ${doc.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void download(doc);
                    }}
                  >
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path
                        d="M10 3v9M6 9l4 4 4-4M4 16h12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
              {tagging === doc.id && (
                <tr className="tagrow">
                  <td colSpan={8}>
                    <TagEditor
                      doc={doc}
                      known={knownTags}
                      onSave={(tags) => saveTags(doc, tags)}
                      onCancel={() => setTagging(null)}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}

            {page?.items.length === 0 && (
              <tr>
                <td colSpan={8} className="content-loading">
                  {tag
                    ? `No document of this well is tagged ${tag}.`
                    : selected?.kind === 'smart'
                    ? 'Nothing matches this saved search yet.'
                    : 'No documents in this folder yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="docpager">
          <button type="button" className="pagebtn" aria-label="First page" onClick={() => goToPage(1)}>
            ⏮
          </button>
          <button
            type="button"
            className="pagebtn"
            aria-label="Previous page"
            onClick={() => goToPage(pageNo - 1)}
          >
            ◀
          </button>
          {Array.from({ length: Math.min(5, pageCount) }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={n === pageNo ? 'pagebtn is-current' : 'pagebtn'}
              onClick={() => goToPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="pagebtn"
            aria-label="Next page"
            onClick={() => goToPage(pageNo + 1)}
          >
            ▶
          </button>
          <button
            type="button"
            className="pagebtn"
            aria-label="Last page"
            onClick={() => goToPage(pageCount)}
          >
            ⏭
          </button>

          <label className="docpager-size">
            Page size:
            <select defaultValue={String(PER_PAGE)} aria-label="Page size" disabled>
              <option>{PER_PAGE}</option>
            </select>
          </label>

          <span className="docpager-spacer" />

          <select className="docpager-filter" defaultValue="Content" aria-label="Search in">
            <option>Content</option>
            <option>Name</option>
          </select>
          <input className="docpager-search" type="search" aria-label="Search documents" />
          <span className="docpager-count">
            {page ? `${page.total} items in ${pageCount} page${pageCount === 1 ? '' : 's'}` : '—'}
          </span>
        </div>

        <div className="docactions">
          <button type="button" className="btn-action">
            Action <span aria-hidden="true">▾</span>
          </button>
          <span className="docpager-spacer" />
          {upload && (
            <span className="docupload" role="status">
              Uploading {upload.done + 1} of {upload.total}: {upload.name}
            </span>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            className="visually-hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => {
              // Copy the list before clearing the input, or the FileList empties
              // with it — and clearing is what lets the same file be picked twice.
              const picked = Array.from(e.target.files ?? []);
              e.target.value = '';
              void uploadFiles(picked);
            }}
          />
          <button
            type="button"
            className="btn-upload"
            title={uploadHint}
            disabled={!canUpload || upload !== null}
            onClick={() => fileInput.current?.click()}
          >
            {upload ? 'Uploading…' : '+ Upload'}
          </button>
        </div>

        {openFile && (
          <Suspense fallback={<SkeletonPage />}>
            <DocPreview
              doc={openFile}
              initialPage={
                openFile.id === Number(params.get('doc')) ? Number(params.get('at')) || 1 : 1
              }
            />
          </Suspense>
        )}
      </section>
      )}
    </div>
  );
}

/**
 * A document's tags as chips, each listing the well's documents that carry
 * it, and — for whoever files the well's documents — the way into the editor.
 * Clicks stop here: the row underneath opens the document.
 */
function DocTags({
  doc,
  editable,
  editing,
  onPick,
  onEdit,
}: {
  doc: DocumentRow;
  editable: boolean;
  editing: boolean;
  onPick: (tag: string) => void;
  onEdit: () => void;
}) {
  const tags = doc.tags ?? [];

  return (
    <>
      {tags.map((t) => (
        <button
          key={t}
          type="button"
          className="tagchip"
          title={`Every document of this well tagged ${t}`}
          onClick={(e) => {
            e.stopPropagation();
            onPick(t);
          }}
        >
          {t}
        </button>
      ))}
      {editable && (
        <button
          type="button"
          className="tagchip tagchip--edit"
          aria-label={`Edit the tags of ${doc.name}`}
          aria-expanded={editing}
          title="Say what data this document holds"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          {tags.length > 0 ? 'Edit' : '+ Tag'}
        </button>
      )}
    </>
  );
}

/** The most tags one document takes, and the longest one; the server's limits. */
const MAX_TAGS = 12;
const MAX_TAG_LENGTH = 40;

/**
 * The tags of one document, being edited: the ones it has as removable chips,
 * a box to type another into, and underneath the tags worth offering — the
 * ones the company already uses first, so the same data is called the same
 * thing on every well.
 *
 * Nothing is saved until Save, so a slip is undone with Cancel.
 */
function TagEditor({
  doc,
  known,
  onSave,
  onCancel,
}: {
  doc: DocumentRow;
  known: string[];
  onSave: (tags: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [tags, setTags] = useState<string[]>(doc.tags ?? []);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const has = (t: string) => tags.some((have) => have.toLowerCase() === t.toLowerCase());
  const offers = known.filter((t) => !has(t)).slice(0, 14);

  // What is typed may be several tags at once, comma separated. Returns the
  // list with them added, or null when one of them cannot be a tag.
  const withTyped = (list: string[], typed: string): string[] | null => {
    const next = [...list];
    for (const raw of typed.split(/[,;|]/)) {
      const t = raw.trim().replace(/\s+/g, ' ');
      if (!t || next.some((have) => have.toLowerCase() === t.toLowerCase())) continue;
      if (t.length > MAX_TAG_LENGTH) {
        setProblem(`A tag is at most ${MAX_TAG_LENGTH} characters.`);
        return null;
      }
      // Shown the way it will be saved: in the spelling already in use.
      next.push(known.find((k) => k.toLowerCase() === t.toLowerCase()) ?? t);
    }
    if (next.length > MAX_TAGS) {
      setProblem(`A document takes at most ${MAX_TAGS} tags.`);
      return null;
    }
    return next;
  };

  const add = (typed: string) => {
    setProblem(null);
    const next = withTyped(tags, typed);
    if (!next) return;
    setTags(next);
    setDraft('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      // Enter in an empty box is Save; with something typed it adds the tag.
      if (e.key === 'Enter' && !draft.trim()) return;
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      setTags(tags.slice(0, -1));
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const save = async () => {
    if (busy) return;
    setProblem(null);

    // Whatever is still in the box counts: nobody means to throw it away.
    const final = withTyped(tags, draft);
    if (!final) return;

    setBusy(true);
    try {
      await onSave(final);
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : 'Could not save the tags.');
      setBusy(false);
    }
  };

  return (
    <form
      className="tageditor"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="tageditor-title">
        What data is in <strong>{doc.name}</strong>?
      </p>

      <div className="tageditor-box">
        {tags.map((t) => (
          <span key={t} className="tagchip tagchip--held">
            {t}
            <button
              type="button"
              aria-label={`Remove the tag ${t}`}
              onClick={() => setTags(tags.filter((have) => have !== t))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={tags.length === 0 ? 'e.g. Mud, Cost, Lithology' : 'Add another…'}
          aria-label="Add a tag"
          maxLength={MAX_TAG_LENGTH * 4}
          autoFocus
        />
      </div>

      {offers.length > 0 && (
        <div className="tageditor-offers">
          <span>Suggested:</span>
          {offers.map((t) => (
            <button key={t} type="button" className="tagchip tagchip--offer" onClick={() => add(t)}>
              + {t}
            </button>
          ))}
        </div>
      )}

      {problem && (
        <p className="tageditor-problem" role="alert">
          {problem}
        </p>
      )}

      <div className="tageditor-actions">
        <button type="submit" className="treetool treetool--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save tags'}
        </button>
        <button type="button" className="treetool" onClick={onCancel}>
          Cancel
        </button>
        <span className="tageditor-hint">
          Enter or a comma adds a tag. The assistant reads the tags, so saving re-reads this
          document in the background.
        </span>
      </div>
    </form>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" className="th-search">
      <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.4 10.4 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function TreeIcon({ kind }: { kind: 'clock' | 'folder' | 'log' }) {
  if (kind === 'log') {
    return (
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
        <path d="M2 1.5v13M8 1.5v13M14 1.5v13" stroke="#8aa4b5" strokeWidth="1" />
        <path
          d="M4.5 1.5 3.4 4l2 2.2-1.6 2.6 1.9 2.3-1.3 3.4M11 1.5l1.2 3-1.8 2.4 2 2.1-1.5 2.4.9 3.1"
          fill="none"
          stroke="#0f766e"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (kind === 'clock') {
    return (
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
        <circle cx="8" cy="8" r="6" fill="none" stroke="#2e7d9e" strokeWidth="1.4" />
        <path d="M8 4.6V8l2.4 1.6" stroke="#2e7d9e" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="treeicon">
      <path d="M1.5 4.2h4.6l1.2 1.5h7.2v7.1H1.5z" fill="#8aa4b5" />
    </svg>
  );
}
